import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatDT, openFor } from "@/lib/format";
import {
  ListChecks, Clock, DoorOpen, X, Loader2, Filter, RefreshCw, Eye, Pencil,
  Save, History, UserCog, ChevronDown, ChevronUp, Search, MessageCircle,
} from "lucide-react";

const STATUSES = ["PENDING", "IN PROGRESS", "CLOSED"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const priorityColor = {
  Urgent: "bg-red-100 text-red-600", High: "bg-orange-100 text-orange-600",
  Normal: "bg-blue-100 text-blue-600", Low: "bg-slate-100 text-slate-500",
};
const statusColor = {
  PENDING: "bg-amber-100 text-amber-700", "IN PROGRESS": "bg-blue-100 text-blue-700",
  CLOSED: "bg-emerald-100 text-emerald-700",
};

// SLA color: green while well within the escalation window, amber once past
// 70% of it, red once actually breached. Closed glitches are neutral.
function slaBadge(g, escalationMinutes) {
  if (g.status === "CLOSED") return { cls: "bg-slate-100 text-slate-500", label: "Closed" };
  const mins = Math.max(0, Math.floor((Date.now() - new Date(g.created_at).getTime()) / 60000));
  const label = openFor(g.created_at);
  if (escalationMinutes > 0 && mins >= escalationMinutes) return { cls: "bg-red-100 text-red-600 font-bold", label };
  if (escalationMinutes > 0 && mins >= escalationMinutes * 0.7) return { cls: "bg-amber-100 text-amber-700 font-semibold", label };
  return { cls: "bg-emerald-100 text-emerald-700", label };
}

export default function StatusBoard() {
  const { user, settings } = useAuth();
  const tz = settings?.general?.timezone || "Asia/Dhaka";
  const escalationMinutes = Number(settings?.general?.escalation_minutes) || 30;
  const [glitches, setGlitches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ status: "PENDING", location: "", sub_location: "", forbes_class: "", priority: "", room_no: "", q: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);
  const [viewG, setViewG] = useState(null);
  const [editG, setEditG] = useState(null);
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/glitches", { params });
      setGlitches(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 60000); return () => clearInterval(t); }, []);

  const onLoc = async (v) => {
    setF((x) => ({ ...x, location: v, sub_location: "", forbes_class: "" }));
    setSubs([]); setClasses([]);
    if (v) { const { data } = await api.get("/classifications/sublocations", { params: { department: v } }); setSubs(data); }
  };
  const onSub = async (v) => {
    setF((x) => ({ ...x, sub_location: v, forbes_class: "" })); setClasses([]);
    if (v) { const { data } = await api.get("/classifications/forbes", { params: { department: f.location, sub_location: v } }); setClasses(data); }
  };
  const reset = () => { setF({ status: "PENDING", location: "", sub_location: "", forbes_class: "", priority: "", room_no: "", q: "", date_from: "", date_to: "" }); setSubs([]); setClasses([]); };

  const fld = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df]";

  return (
    <div className="gm-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 text-[#3a3b45]">
          <div className="w-10 h-10 rounded-xl bg-[#4e73df]/10 flex items-center justify-center text-[#4e73df]"><ListChecks className="w-5 h-5" /></div>
          <div>
            <h2 className="font-bold text-lg leading-none">Glitch Status</h2>
            <p className="text-xs text-slate-400 mt-1">{glitches.length} glitch{glitches.length !== 1 ? "es" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="toggle-filters" onClick={() => setShowFilters((s) => !s)} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50">
            <Filter className="w-4 h-4" /> Filters {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button data-testid="status-refresh" onClick={load} className="p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-500"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-2xl gm-shadow p-5 mb-5 gm-fade-up">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Status</label>
              <select className={fld} data-testid="filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option value="">All</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500">Location</label>
              <select className={fld} data-testid="filter-location" value={f.location} onChange={(e) => onLoc(e.target.value)}><option value="">All</option>{departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500">Sub Location</label>
              <select className={fld} value={f.sub_location} onChange={(e) => onSub(e.target.value)} disabled={!subs.length}><option value="">All</option>{subs.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500">Forbes Class</label>
              <select className={fld} value={f.forbes_class} onChange={(e) => setF({ ...f, forbes_class: e.target.value })} disabled={!classes.length}><option value="">All</option>{classes.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500">Priority</label>
              <select className={fld} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}><option value="">All</option>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500">Room #</label><input className={fld} value={f.room_no} onChange={(e) => setF({ ...f, room_no: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-500">From</label><input type="date" className={fld} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-500">To</label><input type="date" className={fld} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="text-xs font-semibold text-slate-500">Search (description / guest)</label>
              <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input className={`${fld} pl-9`} data-testid="filter-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} /></div></div>
            <div className="flex items-end"><button onClick={reset} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Reset</button></div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#4e73df]" /></div>
      ) : glitches.length === 0 ? (
        <div className="bg-white rounded-2xl gm-shadow py-20 text-center text-slate-400"><p className="font-medium">No glitches match your filters.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {glitches.map((g) => {
            const sla = slaBadge(g, escalationMinutes);
            return (
            <div key={g.id} data-testid={`glitch-card-${g.serial}`} className="bg-white rounded-2xl gm-shadow p-5 flex flex-col hover:-translate-y-0.5 transition-transform">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-lg bg-[#4e73df]/10 text-[#4e73df] flex items-center justify-center"><DoorOpen className="w-4 h-4" /></span>
                  <div><p className="font-bold text-[#3a3b45] leading-none">Room {g.room_no || "-"}</p><p className="text-[11px] text-slate-400 mt-0.5">#{g.serial} · {g.guest_name || "No guest"}</p></div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColor[g.status] || "bg-slate-100 text-slate-500"}`}>{g.status}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${priorityColor[g.priority] || priorityColor.Normal}`}>{g.priority}</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 mb-2"><span className="font-medium text-slate-600">{g.location}</span>{g.sub_location && <> · {g.sub_location}</>}{g.forbes_class && <> · {g.forbes_class}</>}</div>
              <p className="text-sm text-slate-600 flex-1 line-clamp-3 mb-3">{g.description}</p>
              <div className="flex items-center justify-between text-[11px] pt-3 border-t border-slate-100">
                <span data-testid={`sla-badge-${g.serial}`} className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${sla.cls}`}><Clock className="w-3 h-3" /> {sla.label}</span>
                <span className="text-slate-400">by {g.created_by}{g.update_count > 0 && ` · ${g.update_count} update${g.update_count > 1 ? "s" : ""}`}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button data-testid={`view-glitch-${g.serial}`} onClick={() => setViewG(g)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg py-2 hover:bg-slate-50"><Eye className="w-4 h-4" /> View</button>
                <button data-testid={`edit-glitch-${g.serial}`} onClick={() => setEditG(g)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#4e73df] rounded-lg py-2 hover:bg-[#3a5fd0]"><Pencil className="w-4 h-4" /> Modify</button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {viewG && <ViewModal g={viewG} tz={tz} onClose={() => setViewG(null)} />}
      {editG && <EditModal g={editG} user={user} onClose={() => setEditG(null)} onSaved={() => { setEditG(null); load(); }} />}
    </div>
  );
}

function ViewModal({ g, tz, onClose }) {
  const [logs, setLogs] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get(`/glitches/${g.id}/logs`).then(({ data }) => setLogs(data)).catch(() => {}); }, [g.id]);
  const Row = ({ label, value }) => (
    <div className="py-2 border-b border-slate-50">
      <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</span>
      <span className="block text-sm font-medium text-slate-700 break-words whitespace-pre-wrap">{value || "-"}</span>
    </div>
  );
  return (
    <Modal onClose={onClose} title={`Glitch #${g.serial}`} badge={g.status}>
      <div className="p-5 lg:max-w-3xl lg:mx-auto space-y-1" data-testid="view-modal">
        {g.guest_log_thread_id && (
          <button
            onClick={() => navigate("/guest-log", { state: { openThreadId: g.guest_log_thread_id } })}
            data-testid="view-guest-log-link"
            className="w-full mb-3 flex items-center justify-center gap-2 text-sm font-semibold text-[#4e73df] bg-[#4e73df]/10 hover:bg-[#4e73df]/20 rounded-lg px-4 py-2.5 transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> View Guest Log conversation for this room
          </button>
        )}
        <Row label="Room" value={g.room_no} /><Row label="Guest" value={g.guest_name} />
        <Row label="Check-In" value={g.checkin} /><Row label="Check-Out" value={g.checkout} />
        <Row label="Location" value={`${g.location}${g.sub_location ? " / " + g.sub_location : ""}`} />
        <Row label="Forbes Classification" value={g.forbes_class} /><Row label="Priority" value={g.priority} />
        <Row label="Incident" value={g.description} /><Row label="Resolution" value={g.close_remarks} />
        <Row label="Comments" value={g.comments} /><Row label="Created by" value={g.created_by} />
        <Row label="Created at" value={formatDT(g.created_at, tz)} />
        {g.status === "CLOSED" && <><Row label="Closed by" value={g.closed_by} /><Row label="Closed at" value={formatDT(g.closed_at, tz)} /><Row label="Elapsed" value={g.elapsed} /></>}
        <div className="pt-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 mb-2"><History className="w-4 h-4 text-[#4e73df]" /> Update Log</p>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {logs.length === 0 ? <p className="text-xs text-slate-400">No log entries.</p> : logs.map((l) => (
              <div key={l.id} className="text-xs bg-slate-50 rounded-lg px-3 py-2">
                <div className="flex justify-between"><span className="font-semibold text-slate-600">{l.by}</span><span className="text-slate-400">{formatDT(l.at, tz)}</span></div>
                <p className="text-slate-500 mt-0.5">{l.note}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function EditModal({ g, user, onClose, onSaved }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    status: g.status || "PENDING", close_remarks: g.close_remarks || "",
    comments: g.comments || "", informed_to: g.informed_to || "", informed_by: g.informed_by || "",
    priority: g.priority || "Normal",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((x) => ({ ...x, [k]: v }));
  const ro = "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-600";
  const ed = "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20";
  const lbl = "text-[13px] font-semibold text-slate-600 mb-1.5 block";
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/glitches/${g.id}`, form); toast.success(`Glitch #${g.serial} updated by ${data.updated_by || user?.username}`); onSaved(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const Section = ({ title }) => <p className="text-[13px] font-bold text-[#4e73df] border-b border-slate-100 pb-1 mt-2">{title}</p>;
  const footer = (
    <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 shrink-0 bg-white">
      <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
      <button onClick={save} disabled={saving} data-testid="update-glitch-btn" className="px-5 py-2.5 rounded-lg bg-[#4e73df] hover:bg-[#3a5fd0] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Update Glitch</button>
    </div>
  );
  return (
    <Modal onClose={onClose} title={`Modify Glitch #${g.serial}`} badge={g.status} timer={openFor(g.created_at)} footer={footer}>
      <div className="p-5 lg:max-w-3xl lg:mx-auto space-y-3" data-testid="edit-modal">
        {g.guest_log_thread_id && (
          <button
            onClick={() => navigate("/guest-log", { state: { openThreadId: g.guest_log_thread_id } })}
            data-testid="view-guest-log-link-edit"
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#4e73df] bg-[#4e73df]/10 hover:bg-[#4e73df]/20 rounded-lg px-4 py-2.5 transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> View Guest Log conversation for this room
          </button>
        )}
        <Section title="Basic Information" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Room #</label><input className={ro} value={g.room_no} readOnly /></div>
          <div><label className={lbl}>Guest Name</label><input className={ro} value={g.guest_name} readOnly /></div>
        </div>
        <Section title="Location Information" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Location</label><input className={ro} value={g.location} readOnly /></div>
          <div><label className={lbl}>Sub Location</label><input className={ro} value={g.sub_location} readOnly /></div>
        </div>
        <div><label className={lbl}>Forbes Classification</label><input className={ro} value={g.forbes_class} readOnly /></div>
        <Section title="Incident Details" />
        <div><label className={lbl}>Incident</label><textarea className={ro} rows={3} value={g.description} readOnly /></div>
        <div><label className={lbl}>Resolution</label><textarea className={ed} data-testid="edit-resolution" rows={2} value={form.close_remarks} onChange={(e) => set("close_remarks", e.target.value)} placeholder="Action taken" /></div>
        <div><label className={lbl}>Comments</label><textarea className={ed} data-testid="edit-comments" rows={2} value={form.comments} onChange={(e) => set("comments", e.target.value)} placeholder="Comments" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Informed To</label><input className={ed} value={form.informed_to} onChange={(e) => set("informed_to", e.target.value)} /></div>
          <div><label className={lbl}>Informed By</label><input className={ed} value={form.informed_by} onChange={(e) => set("informed_by", e.target.value)} /></div>
        </div>
        <Section title="Status Information" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Glitch Status</label>
            <select className={ed} data-testid="edit-status" value={form.status} onChange={(e) => set("status", e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className={lbl}>Updated By</label>
            <div className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-600 flex items-center gap-2"><UserCog className="w-4 h-4 text-[#4e73df]" /> {user?.username} <span className="text-[11px] text-slate-400">(auto)</span></div></div>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, title, badge, timer, footer }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm sm:p-6" onClick={onClose}>
      <div
        className="bg-white w-full h-full sm:h-[90vh] sm:max-w-4xl sm:rounded-2xl gm-shadow-lg gm-fade-up flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Pencil className="w-4 h-4 text-[#4e73df]" /><h3 className="font-bold text-[#3a3b45]">{title}</h3>
            {badge && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColor[badge] || "bg-slate-100 text-slate-500"}`}>{badge}</span>}
            {timer && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Open for: {timer}</span>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
        {footer}
      </div>
    </div>,
    document.body
  );
}
